import { yupResolver } from "@hookform/resolvers/yup";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CreateProgramParams } from "../../api/services/program/models";
import {
  useCreateProgramMutation,
  useGetProgramDetailById,
} from "../../api/services/program/programApis";
import {
  MuiBox,
  MuiButtonSecondary,
  MuiButtonTertiary,
  MuiModal,
  MuiTypography,
  WarningConfirmationModal,
} from "../../components";
import { InformationBoxWrapper } from "../../components/informationBox/InformationBox.style";
import PermissionWrapper from "../../components/permissionWrapper/PermissionWrapper";
import {
  EMPTY_VALUE_DASH,
  MODULE_PERMISSIONS,
  TIMEOUT_100,
} from "../../helpers/constants";
import { PAGE_IDS, PAGE_URLS } from "../../helpers/constants/pageUrls";
import { commonSiteCoreKeys } from "../../helpers/constants/siteCoreKeys";
import {
  getValueOrBlank,
  isNotEmpty,
  scrollToTopContainer,
} from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import { navigationDataSelector } from "../../redux/slices/appState/appStateSelector";
import {
  setIsDataModifiedCurrentPage,
  setNavigationDataClicked,
  toggleLoader,
  toggleToast,
} from "../../redux/slices/appState/appStateSlice";
import {
  updateOpcoFieldsEditable,
  updateProgramDetails,
  updateProgramStatus,
} from "../../redux/slices/program/programSlice";
import { clearStartChildProgramNavigation } from "../../redux/slices/project/projectSlice";
import { CreateProgramFooter } from "./components/createProgramFooter/CreateProgramFooter";
import { CreateProgramGeneralDetail } from "./components/createProgramGeneralDetail/CreateProgramGeneralDetail";
import { CreateProgramInformationModal } from "./components/createProgramInformationModal/CreateProgramInformationModal";
import { CreateProgramKeyInput } from "./components/createProgramKeyInput/CreateProgramKeyInput";
import { CreateProgramTabs } from "./components/createProgramTabs/CreateProgramTabs";
import { InvoiceHistory } from "./components/invoiceHistory/InvoiceHistoryMain";
import { CreateProgramWrapper } from "./CreateProgram.style";
import {
  CreateProgramFormKey,
  CreateProgramKeyTypes,
  ProgramTabFormType,
} from "./modelsAndSchemas/Models";
import { CreateProgramSchema } from "./modelsAndSchemas/ValidationSchema";
import { programSiteCoreKeys } from "./siteCore/SiteCoreKeys";

interface CreateProgramProps {
  isEditProgram?: boolean;
  pageId?: string;
  programId?: string;
  editProgramData?: ProgramTabFormType;
  tabValue?: number;
  isFromTransfer?: boolean;
  isFromEditProjectChildPrograms?: boolean;
}

const CreateProgram: FC<CreateProgramProps> = ({
  pageId = PAGE_IDS.CREATE_PROGRAM,
  isEditProgram = false,
  programId = "",
  editProgramData,
  tabValue = 0,
  isFromTransfer,
  isFromEditProjectChildPrograms,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigationData = useSelector(navigationDataSelector);
  const {
    pageTitle,
    commonValidationMessages,
    commonLabels,
    fetchLabel,
    fetchCommonLabel,
    fetchRichTexts,
  } = useSiteCore();
  const [isProgramCreated, setIsProgramCreated] = useState(isEditProgram);
  const [showInformationModal, setShowInformationModal] = useState(false);
  const [showInvoiceHistory, setShowInvoiceHistory] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showNavigateConfirmationModal, setShowNavigateConfirmationModal] =
    useState(false);
  const [isTabDirty, setIsTabDirty] = useState(false);
  const [showExitConfirmationModal, setShowExitConfirmationModal] =
    useState(false);
  const [projectIdForNavigation, setProjectIdForNavigation] = useState("");

  const isFromNIS = location.state?.isFromNISParticipant ?? false;

  const {
    mutateAsync: saveProgramApi,
    isPending: isSavePending,
    isError: isSaveError,
    isSuccess: isSaveSuccess,
    data: createdProgramData,
    error: saveProgramError,
  } = useCreateProgramMutation();

  const {
    data: programDetail,
    isFetching: isProgramFetching,
    isError: isProgramError,
    refetchWithParams: refetchProgramDetail,
  } = useGetProgramDetailById();

  useEffect(() => {
    if (isProgramError || isSaveError) {
      if (isSaveError) {
        setShowInformationModal(false);
      }
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isProgramError, isSaveError]);

  const defaultValues = {
    [CreateProgramFormKey.client]: "",
    [CreateProgramFormKey.deliveryCountry]: "",
    [CreateProgramFormKey.lineOfBusiness]: "",
    [CreateProgramFormKey.programTemplate]: "",
    [CreateProgramFormKey.opportunityId]: "",
    [CreateProgramFormKey.opportunityName]: "",
    [CreateProgramFormKey.isOpportunityIdValidated]: false,
    [CreateProgramFormKey.alternateName]: EMPTY_VALUE_DASH,
    [CreateProgramFormKey.salesOfficeId]: "",
    [CreateProgramFormKey.isProjectTagged]: false,
    [CreateProgramFormKey.projectKey]: "",
    [CreateProgramFormKey.projectName]: "",
    [CreateProgramFormKey.nextPersona]: EMPTY_VALUE_DASH,
  };

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isSavePending,
      }),
    );

    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isSavePending]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isProgramFetching: {
          isVisible: isProgramFetching,
        },
      }),
    );

    return () => {
      dispatch(toggleLoader({ isProgramFetching: { isVisible: false } }));
    };
  }, [isProgramFetching]);

  useEffect(() => {
    if (navigationData.onNavigationClicked) {
      setShowExitConfirmationModal(true);
    }
  }, [navigationData.onNavigationClicked]);

  useEffect(() => {
    if (isSaveSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            programSiteCoreKeys.keyProgramDetails,
            programSiteCoreKeys.programCreateSuccessMessage,
          ),
        }),
      );
      setShowInformationModal(false);
      setIsProgramCreated(true);
      scrollToTopContainer();
    }
  }, [isSaveSuccess]);

  useEffect(() => {
    if (isNotEmpty(createdProgramData)) {
      refetchProgramDetail(createdProgramData?.id);
    }
  }, [createdProgramData]);

  useEffect(() => {
    if (isEditProgram && programId) {
      refetchProgramDetail(programId);
    }
  }, [isEditProgram]);

  const methods = useForm<CreateProgramKeyTypes>({
    mode: "all",
    defaultValues: defaultValues,
    resolver: yupResolver(CreateProgramSchema(commonValidationMessages)),
  });

  const {
    handleSubmit,
    formState: { isDirty, errors },
    reset,
    getValues,
    setError,
    trigger,
  } = methods;
  useEffect(() => {
    return () => {
      dispatch(setIsDataModifiedCurrentPage(false));
    };
  }, []);

  useEffect(() => {
    const isShowConfirmationPopup = isDirty || isFromNIS;
    dispatch(setIsDataModifiedCurrentPage(isShowConfirmationPopup));
  }, [isDirty, isFromNIS]);

  const handleContinueClick = () => {
    handleSubmit(() => setShowInformationModal(true))();
  };

  const checkAndValidateOpportunityId = (): boolean => {
    if (!!programDetail?.isOpportunityIdFromBillingConfiguration) return true;
    const { opportunityId, isOpportunityIdValidated } = getValues();

    if (isNotEmpty(opportunityId) && !isOpportunityIdValidated) {
      if (!errors?.opportunityId) {
        setError(CreateProgramFormKey.opportunityId, {
          type: "custom",
          message: fetchLabel(
            programSiteCoreKeys.keyProgramDetails,
            programSiteCoreKeys.opportunityIDMustBeValidatedMessage,
          ),
        });
      }
      return false;
    }

    return true;
  };

  const handleSaveButtonClick = () => {
    if (!checkAndValidateOpportunityId()) {
      trigger([
        "client",
        "deliveryCountry",
        "lineOfBusiness",
        "programTemplate",
      ]);
    } else if (!isProgramCreated) {
      handleContinueClick();
    }
  };

  const handleNavigateBack = () => {
    location.state?.from === PAGE_URLS.SEARCH_PROGRAM ||
    location.pathname.includes(PAGE_URLS.EDIT_PROGRAM)
      ? navigate(-1)
      : navigate(PAGE_URLS.DASHBOARD);
  };

  const handleConfirmationModalPrimaryClick = () => {
    setShowConfirmationModal(false);
    goBack();
  };

  const handleBackFromTabs = (shouldShowConfirmation?: boolean) => {
    if (shouldShowConfirmation) {
      setShowConfirmationModal(true);
    } else {
      goBack();
    }
  };

  const handleDirtyTabCallBack = (isDirty: boolean) => {
    setIsTabDirty(isDirty);
  };

  const onNavigateToProjectCallBack = (projectId: string = "") => {
    if (isTabDirty) {
      setProjectIdForNavigation(projectId);
      setShowNavigateConfirmationModal(true);
    } else {
      navigateToProject(projectId);
    }
  };

  const handleNavigateConfirmationModalPrimaryClick = () => {
    setShowNavigateConfirmationModal(false);
    navigateToProject(projectIdForNavigation);
  };

  const navigateToProject = (projectId: string = "") => {
    navigate(`${PAGE_URLS.VIEW_PROJECT}/${projectId}`, {
      state: {
        projectId: projectId,
        programId: programId,
        from: PAGE_URLS.EDIT_PROGRAM,
      },
    });
  };
  //below 2 function to create program with general detail
  const getDataForApiParam = () => {
    const {
      client,
      deliveryCountry,
      lineOfBusiness,
      programTemplate,
      salesOfficeId,
      alternateName,
      isProjectTagged,
      projectKey,
      opportunityId,
      opportunityName,
    } = getValues();

    let params: CreateProgramParams = {
      clientId: client,
      countryId: deliveryCountry,
      lobId: lineOfBusiness,
      programTemplateId: programTemplate,
      salesOfficeId: salesOfficeId,
      requestingUserName: "",
      alternateDisplayName: alternateName,
      isProjectTagged: isProjectTagged,
      projectId: isProjectTagged && isNotEmpty(projectKey) ? projectKey : "",
      opportunityId,
      opportunityName,
    };

    if (isFromNIS) {
      params = {
        ...params,
        nisParticipantId: getValueOrBlank(
          location.state?.nisParticipantData?.id,
        ),
      };
    }
    return params;
  };

  const handleSaveGeneralDetail = () => {
    saveProgramApi(getDataForApiParam());
  };

  useEffect(() => {
    dispatch(
      updateProgramStatus(getValueOrBlank(programDetail?.programStatus)),
    );
    dispatch(
      updateProgramDetails({
        clientId: getValueOrBlank(programDetail?.clientId),
        billingCountryId: getValueOrBlank(programDetail?.billingCountryId),
        programTemplateId: getValueOrBlank(programDetail?.programTemplateId),
      }),
    );
    dispatch(
      updateOpcoFieldsEditable(programDetail?.areOpcoFieldsEditable ?? true),
    );
  }, [editProgramData, programDetail]);

  const goBack = () => {
    navigate(-1);
  };

  const handleBackButtonClick = () => {
    handleBackFromTabs(isTabDirty);
  };

  const handleExitConfirmationModalClose = () => {
    dispatch(
      setNavigationDataClicked({
        ...navigationData,
        onNavigationClicked: false,
      }),
    );
    if (showExitConfirmationModal) {
      setShowExitConfirmationModal(false);
    }
  };
  const handleExitConfirmationModalPrimaryButtonClick = () => {
    dispatch(
      setNavigationDataClicked({
        ...navigationData,
        onNavigationClicked: false,
      }),
    );
    dispatch(setIsDataModifiedCurrentPage(false));
    dispatch(clearStartChildProgramNavigation()); // Clear Child Program Navigation state on sidebar navigation
    reset();
    setTimeout(() => {
      navigate(navigationData.navigationPath);
    }, TIMEOUT_100);
    setShowExitConfirmationModal(false);
  };

  return (
    <CreateProgramWrapper data-testid="create-program">
      {isFromEditProjectChildPrograms && (
        <MuiButtonTertiary
          data-testid={`${pageId}-back`}
          onClick={handleBackButtonClick}
        >
          <ArrowBackIosIcon />
          {commonLabels.back}
        </MuiButtonTertiary>
      )}
      <FormProvider {...methods}>
        <InformationBoxWrapper className="information-box">
          <MuiBox className="line" />
          <MuiTypography variant="h1Bold" className="text" component="h1">
            {isEditProgram
              ? fetchLabel(
                  programSiteCoreKeys.createProgramHeader,
                  programSiteCoreKeys.editProgramTitle,
                )
              : pageTitle}
          </MuiTypography>
          {isEditProgram && (
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.PROGRAM_VIEW_INVOICE_HISTORY}
            >
              <MuiBox className="view-invoice-btn">
                <MuiButtonSecondary onClick={() => setShowInvoiceHistory(true)}>
                  {fetchLabel(
                    programSiteCoreKeys.createProgramHeader,
                    programSiteCoreKeys.viewInvoiceHistoryButton,
                  )}
                </MuiButtonSecondary>
              </MuiBox>
            </PermissionWrapper>
          )}
        </InformationBoxWrapper>
        {!isProgramCreated ? (
          <CreateProgramKeyInput
            pageId={pageId}
            apiErrorData={saveProgramError}
            programId={programId}
          />
        ) : (
          <MuiBox>
            <CreateProgramGeneralDetail
              pageId={pageId}
              programDetail={programDetail}
              programId={
                isEditProgram ? programId : (createdProgramData?.id ?? "")
              }
              refetchProgramDetail={refetchProgramDetail}
              isEditProgram={isEditProgram}
              isFromEditProjectChildPrograms={isFromEditProjectChildPrograms}
              onNavigateToProjectCallBack={onNavigateToProjectCallBack}
            />
            <CreateProgramTabs
              pageId={pageId}
              programDetail={programDetail}
              handleNavigateBack={handleBackFromTabs}
              programId={
                isEditProgram ? programId : (createdProgramData?.id ?? "")
              }
              isEditProgram={isEditProgram}
              editProgramData={editProgramData}
              tabValue={tabValue}
              isFromTransfer={isFromTransfer}
              onRefreshProgramDetail={refetchProgramDetail}
              isFromEditProjectChildPrograms={isFromEditProjectChildPrograms}
              handleDirtyTabCallBack={handleDirtyTabCallBack}
            />
          </MuiBox>
        )}

        {!isProgramCreated && (
          <CreateProgramFooter
            pageId={pageId}
            onConfirmClick={handleSaveButtonClick}
            handleNavigateBack={handleNavigateBack}
            isFiledUpdated={isDirty}
            confirmButtonText={commonLabels.continue}
          />
        )}
        {showInformationModal && (
          <CreateProgramInformationModal
            showModal={showInformationModal}
            handleModalClose={() => setShowInformationModal(false)}
            handlePrimaryButtonClick={handleSaveGeneralDetail}
          />
        )}

        {showInvoiceHistory && (
          <MuiModal
            hasLargeContent
            open={showInvoiceHistory}
            onClose={() => setShowInvoiceHistory(false)}
            title={fetchCommonLabel(commonSiteCoreKeys.invoiceHistory)}
            primaryButtonTitle={commonLabels.ok}
            onPrimaryButtonClick={() => setShowInvoiceHistory(false)}
          >
            <InvoiceHistory programId={programId} isViewOnly={!isEditProgram} />
          </MuiModal>
        )}

        {showNavigateConfirmationModal && (
          <WarningConfirmationModal
            show={showNavigateConfirmationModal}
            modalTitle={commonLabels.confirmation}
            primaryButtonTitle={commonLabels.yes}
            secondaryButtonTitle={commonLabels.no}
            onClose={() => setShowNavigateConfirmationModal(false)}
            onPrimaryButtonClick={handleNavigateConfirmationModalPrimaryClick}
            isLabelHTMLContent
            labelContent={fetchRichTexts(
              programSiteCoreKeys.keyProgramDetails,
              programSiteCoreKeys.movingOnToAnotherPageConfirmationMessage,
            )}
          />
        )}

        {showConfirmationModal && (
          <WarningConfirmationModal
            show={showConfirmationModal}
            modalTitle={commonLabels.confirmation}
            primaryButtonTitle={commonLabels.yes}
            secondaryButtonTitle={commonLabels.no}
            onClose={() => setShowConfirmationModal(false)}
            onPrimaryButtonClick={handleConfirmationModalPrimaryClick}
            labelContent={commonLabels.discardChangesText}
          />
        )}
        {showExitConfirmationModal && (
          <WarningConfirmationModal
            labelContent={commonLabels.leavingPageActionText}
            show={showExitConfirmationModal}
            onClose={handleExitConfirmationModalClose}
            onPrimaryButtonClick={handleExitConfirmationModalPrimaryButtonClick}
            primaryButtonTitle={commonLabels.yes}
            secondaryButtonTitle={commonLabels.no}
          />
        )}
      </FormProvider>
    </CreateProgramWrapper>
  );
};

export default CreateProgram;
